//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import 'devextreme/ui/button'
import 'devextreme/ui/load_indicator'
import 'devextreme/ui/popup'
import 'devextreme/ui/popover'
import 'devextreme/ui/text_area'

//#endregion

import { Api, LocalDictionaryFactory } from '../../api/api.module'
import { CommonHelper } from '../../../helpers/common-helper'
import { i18n } from '../../common/i18n/common.i18n'
import { ValidationScriptModel } from '../../api/api.models'
import { TemplateHelper, ConfigHelper, IUserSession } from '../../../helpers'

//#region Asset Imports

import './popup-script.scss'
import * as TEMPLATE from './popup-script.tpl.html'

//#endregion

export interface PopupScriptScope extends ng.IScope {
    elementId: number
    databaseType: number
    propertyName: string
    current: string
    callback: (elementId: number, propertyName: string, scriptText: string) => void

    scriptButtonOptions: DevExpress.ui.dxButtonOptions
    popupOptions: DevExpress.ui.dxPopupOptions
    applyButtonOptions: DevExpress.ui.dxButtonOptions
    cancelButtonOptions: DevExpress.ui.dxButtonOptions
    scriptAreaOptions: DevExpress.ui.dxTextAreaOptions
    validateButtonOptions: DevExpress.ui.dxButtonOptions
    closeButtonOptions: DevExpress.ui.dxButtonOptions
    infoPopoverOptions: DevExpress.ui.dxPopoverOptions

    scriptButton: DevExpress.ui.dxButton
    popup: DevExpress.ui.dxPopup
    applyButton: DevExpress.ui.dxButton
    cancelButton: DevExpress.ui.dxButton
    scriptArea: DevExpress.ui.dxTextArea

    isValidated: boolean
    title: string
    infoPopoverVisible: boolean

    isLoading: boolean
    validateButtonDisabled: () => boolean
    executionTime: string

    currentDecoded: string
}

export class PopupScriptDirective implements ng.IDirective {
    isPopupVisible: boolean

    constructor() { }

    restrict = 'E'
    scope = {
        elementId: '=',
        databaseType: '=',
        propertyName: '@',
        current: '@',
        callback: '='
    }

    controller = ['$scope', '$q', '$timeout', 'api', 'localDictionary', 'userSession', ($scope: PopupScriptScope, $q: ng.IQService, $timeout: ng.ITimeoutService, api: Api, localDictionary: LocalDictionaryFactory, userSession: IUserSession) => {
        let oldScript

        $scope.currentDecoded = CommonHelper.Base64.decode($scope.current)

        $scope.title = i18n('Popup', 'ScriptEdit')

        const roundExecutionTime = (time: string) => {
            const splitted = time.split('.')

            if (splitted.length !== 2)
                return '00:00:00.00'

            let roundedTime = splitted[0] + '.'
            roundedTime += splitted[1].substr(0, 3)
            return roundedTime
        }

        const validateScript = (script: string) => {
            const model = new ValidationScriptModel()
            model.text = script
            model.databaseType = $scope.databaseType || 1

            $scope.isLoading = true
            $scope.executionTime = null

            api.dashboard.validateScript(model).then(result => {
                $scope.executionTime = roundExecutionTime(result.executionTime)
                if (result.isCompleted) {
                    $scope.isValidated = true

                    if (result.error)
                        CommonHelper.openInfoPopup(result.error, 'info', 5000)
                }
                else {
                    $scope.isValidated = false
                    CommonHelper.openInfoPopup(result.error, 'warning', 5000)
                }
            }).finally(() => {
                $scope.isLoading = false
            })
        }

        $scope.isValidated = true

        $scope.scriptButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.EDIT, userSession),
            hint: i18n('Common', 'Edit'),
            onInitialized: e => {
                $scope.scriptButton = e.component
            },
            onClick: e => {
                this.isPopupVisible = !this.isPopupVisible
                $scope.popup.toggle(this.isPopupVisible)
            }
        }

        $scope.popupOptions = {
            width: 800,
            height: 500,
            resizeEnabled: true,
            onInitialized: e => {
                $scope.popup = e.component
            },
            onHidden: e => {
                this.isPopupVisible = false
                $scope.currentDecoded = oldScript
            },
            onShown: e => {
                oldScript = $scope.currentDecoded
            }
        }

        $scope.closeButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.CLOSE, userSession),
            onClick: e => {
                $scope.popup.hide()
            }
        }

        $scope.infoPopoverOptions = {
            width: 400,
            target: '.info-icon',
            bindingOptions: {
                visible: 'infoPopoverVisible'
            }
        }

        $scope.scriptAreaOptions = {
            height: '100%',
            width: '100%',
            bindingOptions: {
                value: 'currentDecoded'
            },
            onInput: e => {
                $scope.isValidated = false
            }
        }

        $scope.validateButtonOptions = {
            text: i18n('Common', 'Validate'),
            height: 34,
            bindingOptions: {
                disabled: 'validateButtonDisabled()'
            },
            onClick: e => {
                validateScript($scope.currentDecoded)
            }
        }

        $scope.applyButtonOptions = {
            text: i18n('Common', 'Apply'),
            height: 34,
            onInitialized: e => {
                $scope.applyButton = e.component
            },
            bindingOptions: {
                disabled: '!isValidated'
            },
            onClick: e => {
                oldScript = $scope.currentDecoded
                if ($scope.callback)
                    $scope.callback($scope.elementId, $scope.propertyName, $scope.currentDecoded)

                $scope.popup.hide()
            }
        }

        $scope.cancelButtonOptions = {
            text: i18n('Common', 'Cancel'),
            height: 34,
            onInitialized: e => {
                $scope.cancelButton = e.component
            },
            onClick: e => {
                $scope.popup.hide()
            }
        }

        $scope.validateButtonDisabled = () => {
            return $scope.isValidated || $scope.isLoading
        }
    }]
    template = TEMPLATE
    transclude = true
}